import { type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { CardGrid, CardGridArea } from "widgets";

import { JobStatusEnum } from "@/graphql.ts";
import { type IJobState, isBlastJob, markJobAsRead } from "@/redux/modules/job.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { VIEW } from "@/settings/settings.js";

import { ButtonHeader } from "../core/ButtonHeader.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import { JobBlastInputCard } from "../entities/cards/JobBlastInputCard.js";
import { JobStatusCard } from "../entities/cards/JobStatusCard.js";
import QueryInfoCard from "../entities/cards/QueryInfoCard.js";
import BlastPageHeader from "./BlastPageHeader.js";

/*
 * The view inside of JobDetailPage. The children are cards for the current job.
 */
const JobSummaryView: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  const { jobId } = useParams<"jobId">();
  const { job, isLoading } = useSelector<IAppState, IJobState>((state) => state.job);
  const showSpinner = isLoading || !job;
  const [currentJobStatus, setJobStatus] = useState<string>(job?.status ?? "");

  useEffect(() => {
    if (currentJobStatus !== job.status) {
      // if status becomes COMPLETED, redirect to the results and mark the notification as read
      if (job && job.status === JobStatusEnum.Completed) {
        if ((job.resultCount ?? 0) > 0) {
          // ...with only one single result we forward the user directly to the dedicated results page
          if (job.results?.length === 1) {
            navigate(`/job/${jobId}/results/${job.results[0]?.id}/summary`);
          } else {
            navigate(`/job/${jobId}/results`);
          }
        }

        // mark job as read of the job is completed and is being viewed
        if (job.notification && !job.notification.read) {
          dispatch(markJobAsRead(job.id));
        }
      }
      setJobStatus(job.status);
    }
  }, [job?.status]);

  return (
    <>
      <BlastPageHeader
        job={job}
        subtitle={"Job Summary"}
        actionWidgets={[
          <ButtonHeader
            viewType={VIEW.BLAST_JOB_VIEW.type}
            hideBlastBtn={true}
            hideExportBtn={true}
            key={"job-summary-view-action-1"}
          />,
        ]}
        isLoading={showSpinner}
      />
      <CardGrid key="card-job" isLoading={showSpinner} className={styleLayout.page}>
        {job && (
          <>
            <QueryInfoCard area={CardGridArea.Left} job={job} />
            <JobStatusCard area={CardGridArea.Right} job={job} />
            {isBlastJob(job) && <JobBlastInputCard area={CardGridArea.Right} job={job} />}
          </>
        )}
      </CardGrid>
    </>
  );
};

export default JobSummaryView;
